import { Navigate, useLocation, type RouteObject } from "react-router-dom";
import NotFound from "../pages/NotFound";
import Layout from "../components/feature/Layout";
import LandingPage from "../pages/landing/page";
import LoginPage from "../pages/login/page";
import { hasAnyPermission, isAuthenticated, loginPath } from "../lib/auth";

function RequireAuth({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  if (!isAuthenticated()) {
    return <Navigate to={loginPath(location.pathname + location.search)} replace />;
  }
  return <>{children}</>;
}

function RequirePermission({
  permissions,
  children,
}: {
  permissions: string[];
  children: React.ReactNode;
}) {
  if (!hasAnyPermission(...permissions)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

function HomeRedirect() {
  const host = window.location.hostname;
  // O painel responde em app.<plataforma>.<dominio> e no desenvolvimento local.
  // Casado pelo prefixo, e nao pelo hostname inteiro: trocar de dominio nao
  // deve exigir alteracao de codigo seguida de rebuild do bundle.
  if (host.startsWith("app.") || host === "localhost" || host === "127.0.0.1") {
    return <Dashboard />;
  }
  return <Navigate to="/landing" replace />;
}
import PublicPortalPage from "../pages/publico/home/page";
import PublicMapPage from "../pages/publico/mapa/page";
import PublicInventoryPage from "../pages/publico/inventario/page";
import PublicInspectionPage from "../pages/publico/vistoria/page";
import PublicOccurrencesPage from "../pages/publico/ocorrencias/page";
import PublicCampaignsPage from "../pages/publico/campanhas/page";
import PublicParksPage from "../pages/publico/parques/page";
import PublicOpenDataPage from "../pages/publico/dados-abertos/page";
import PublicTransparencyPage from "../pages/publico/transparencia/page";
import PublicDossierPage from "../pages/publico/dossie/page";
import ColetorPage from "../pages/coletor/page";
import ColetorTreePage from "../pages/coletor/exemplar/page";
import ManualPage from "../pages/manual/page";
import Dashboard from "../pages/dashboard/page";
import Projetos from "../pages/projetos/page";
import ProjectDetail from "../pages/projetos/detail/page";
import NovoProjeto from "../pages/projetos/novo/page";
import CTA from "../pages/cta/page";
import NovoExemplarPage from "../pages/cta/novo/page";
import FCAPage from "../pages/cta/fca/page";
import Mapa from "../pages/mapa/page";
import Vistorias from "../pages/vistorias/page";
import TerritorioInspecoesPage from "../pages/territorio/inspecoes/page";
import TerritorioBairrosPage from "../pages/territorio/bairros/page";
import TerritorioQuadrasPage from "../pages/territorio/quadras/page";
import TerritorioArvoresPage from "../pages/territorio/arvores/page";
import TerritorioIndicadoresPage from "../pages/territorio/indicadores/page";
import TerritorioConsoleWebgisPage from "../pages/territorio/console-webgis/page";
import AreasVerdes from "../pages/ambiental/areas-verdes/page";
import UCs from "../pages/ambiental/ucs/page";
import Carbono from "../pages/carbono/page";
import Documentos from "../pages/documentos/page";
import Partes from "../pages/partes/page";
import Responsabilidades from "../pages/responsabilidades/page";
import Relatorios from "../pages/relatorios/page";
import Configuracoes from "../pages/configuracoes/page";
import SpeciesProfilePage from "../pages/especies/ficha/page";
import Ambiental from "../pages/ambiental/page";
import LadmMethodology from "../pages/ladm/page";
import PointCloudPage from "../pages/nuvem3d/page";
import DataQualityPage from "../pages/qualidade/page";
import OperationalFlowPage from "../pages/operacional/page";
import MemorialDescritivoPage from "../pages/memorial/page";
import PilotoSBVPage from "../pages/piloto-sbv/page";
import ArborInspectionPage from "../pages/cta/inspecoes/page";
import CampanhasPage from "../pages/campanhas/page";
import TreeDossierPage from "../pages/arvore/dossie/page";
import FCAPrintPage from "../pages/cta/fca/print/page";
import TreeLabelPage from "../pages/cta/fca/etiqueta/page";

const routes: RouteObject[] = [
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    path: "/landing",
    element: <LandingPage />,
  },
  {
    path: "/publico",
    element: <PublicPortalPage />,
  },
  {
    path: "/publico/mapa",
    element: <PublicMapPage />,
  },
  {
    path: "/publico/inventario",
    element: <PublicInventoryPage />,
  },
  {
    path: "/publico/vistoria",
    element: <PublicInspectionPage />,
  },
  {
    path: "/publico/ocorrencias",
    element: <PublicOccurrencesPage />,
  },
  {
    path: "/publico/campanhas",
    element: <PublicCampaignsPage />,
  },
  {
    path: "/publico/parques",
    element: <PublicParksPage />,
  },
  {
    path: "/publico/dados-abertos",
    element: <PublicOpenDataPage />,
  },
  {
    path: "/publico/transparencia",
    element: <PublicTransparencyPage />,
  },
  {
    path: "/publico/dossie",
    element: <PublicDossierPage />,
  },
  {
    // FCA em A4 para "Gerar PDF": fora do Layout, sem menu.
    path: "/cta/:id/pdf",
    element: <RequireAuth><FCAPrintPage /></RequireAuth>,
  },
  {
    // Etiqueta com QR Code para fixar no exemplar: fora do Layout, como o PDF.
    path: "/cta/:id/etiqueta",
    element: <RequireAuth><TreeLabelPage /></RequireAuth>,
  },
  {
    // Manuais de uso (CTA e coletor), fora do layout para abrir bem no celular.
    path: "/manual/:slug",
    element: <ManualPage />,
  },
  {
    path: "/arvore/:id/dossie",
    element: <TreeDossierPage />,
  },
  {
    path: "/coletor",
    element: <ColetorPage />,
  },
  {
    path: "/coletor/nova-arvore",
    element: <ColetorPage />,
  },
  {
    path: "/coletor/vistoria",
    element: <ColetorPage />,
  },
  {
    path: "/coletor/fotos",
    element: <ColetorPage />,
  },
  {
    path: "/coletor/mapa",
    element: <ColetorPage />,
  },
  {
    path: "/coletor/rascunhos",
    element: <ColetorPage />,
  },
  {
    path: "/coletor/sincronizacao",
    element: <ColetorPage />,
  },
  {
    path: "/coletor/qrcode",
    element: <ColetorPage />,
  },
  {
    path: "/coletor/exemplar",
    element: <ColetorPage />,
  },
  {
    // Fotos de um exemplar ja cadastrado (foto principal e fotos de vistoria).
    path: "/coletor/exemplar/:id",
    element: <ColetorTreePage />,
  },
  {
    path: "/coletor/fca",
    element: <ColetorPage />,
  },
  {
    path: "/",
    element: <RequireAuth><Layout /></RequireAuth>,
    children: [
      {
        index: true,
        element: <HomeRedirect />,
      },
      {
        path: "dashboard",
        element: <Dashboard />,
      },
      {
        path: "projetos",
        element: <Projetos />,
      },
      {
        path: "projetos/novo",
        element: <NovoProjeto />,
      },
      {
        path: "projetos/:id",
        element: <ProjectDetail />,
      },
      {
        path: "cta",
        element: <CTA />,
      },
      {
        path: "especies/:id",
        element: <SpeciesProfilePage />,
      },
      {
        path: "cta/novo",
        element: <NovoExemplarPage />,
      },
      {
        path: "cta/:id",
        element: <FCAPage />,
      },
      {
        path: "cta/:treeId/inspecoes/nova",
        element: <ArborInspectionPage />,
      },
      {
        path: "cta/:treeId/inspecoes/:inspectionId",
        element: <ArborInspectionPage />,
      },
      {
        path: "mapa",
        element: <Mapa />,
      },
      {
        path: "vistorias",
        element: <Vistorias />,
      },
      {
        path: "campanhas",
        element: <CampanhasPage />,
      },
      {
        path: "territorio/inspecoes",
        element: <TerritorioInspecoesPage />,
      },
      {
        path: "territorio/bairros",
        element: <TerritorioBairrosPage />,
      },
      {
        path: "territorio/quadras",
        element: <TerritorioQuadrasPage />,
      },
      {
        path: "territorio/arvores",
        element: <TerritorioArvoresPage />,
      },
      {
        path: "territorio/indicadores",
        element: <TerritorioIndicadoresPage />,
      },
      {
        path: "territorio/console-webgis",
        element: <RequirePermission permissions={["gis.write"]}><TerritorioConsoleWebgisPage /></RequirePermission>,
      },
      {
        path: "ambiental",
        element: <Ambiental />,
      },
      {
        path: "ambiental/areas-verdes",
        element: <AreasVerdes />,
      },
      {
        path: "ambiental/ucs",
        element: <UCs />,
      },
      {
        path: "carbono",
        element: <Carbono />,
      },
      {
        path: "documentos",
        element: <Documentos />,
      },
      {
        path: "partes",
        element: <Partes />,
      },
      {
        path: "responsabilidades",
        element: <Responsabilidades />,
      },
      {
        path: "relatorios",
        element: <Relatorios />,
      },
      {
        path: "configuracoes",
        element: <Configuracoes />,
      },
      {
        path: "metodologia",
        element: <LadmMethodology />,
      },
      {
        path: "nuvem3d",
        element: <PointCloudPage />,
      },
      {
        path: "qualidade",
        element: <DataQualityPage />,
      },
      {
        path: "operacional",
        element: <OperationalFlowPage />,
      },
      {
        path: "memorial",
        element: <MemorialDescritivoPage />,
      },
      {
        path: "piloto/sbv",
        element: <PilotoSBVPage />,
      },
    ],
  },
  {
    path: "/app",
    element: <RequireAuth><Layout /></RequireAuth>,
    children: [
      {
        index: true,
        element: <Dashboard />,
      },
      {
        path: "dashboard",
        element: <Dashboard />,
      },
      {
        path: "cta",
        element: <CTA />,
      },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
